import React, { useState } from 'react'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'

import UIButton from '../../../../components/UI/UIButton'
import UITextArea from '../../../../components/UI/UITextArea'
import Loading from '../../../../components/UI/Loading'
import { formatDate } from '../../../../utils/utils'
import {
  whistleblowingCommentResponse,
  whistleblowingData,
} from '../../../../utils/types'
import {
  addWistleBlowingComments,
  getSingleWistleBlowingById,
  getWistleBlowingComments,
  updateWhistleBlowingStatus,
} from '../../../../api/lead/whistleblowing'

interface whistleblowingCaseResponse {
  status: string
  data: { data: whistleblowingData; canManage?: boolean }
}

interface WhistleblowingCaseViewProps {
  id: string
  schoolId: string
  onBack: () => void
  onUpdated: () => void
}

// One report. Its case handler (e.g. the Governor who receives reports about the
// whistleblowing officer) can also close or reopen it and reply to the reporter.
const WhistleblowingCaseView = ({
  id,
  schoolId,
  onBack,
  onUpdated,
}: WhistleblowingCaseViewProps) => {
  const [reply, setReply] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const report = useQuery(['whistleblowingCase', id], async () => {
    const response = (await getSingleWistleBlowingById(
      id,
      schoolId
    )) as whistleblowingCaseResponse
    return response?.data
  })
  const comments = useQuery(['whistleblowingCaseComments', id], async () => {
    const response = (await getWistleBlowingComments(
      id
    )) as whistleblowingCommentResponse
    return response?.data
  })

  const record = report.data?.data
  const canManage = !!report.data?.canManage
  const isClosed = record?.status === 'closed'

  const run = async (
    action: () => Promise<unknown>,
    success: string,
    after: () => void
  ): Promise<void> => {
    setIsSaving(true)
    try {
      const response = (await action()) as { status?: string }
      if (response?.status !== 'successful') throw new Error()
      toast.success(success, { position: 'top-right' })
      after()
    } catch (error) {
      toast.error('Could not update the report. Please try again.', {
        position: 'top-right',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const changeStatus = (): Promise<void> =>
    run(
      () =>
        updateWhistleBlowingStatus(
          { status: isClosed ? 'open' : 'closed' },
          id
        ),
      isClosed ? 'Report reopened.' : 'Report closed.',
      () => {
        report.refetch()
        onUpdated()
      }
    )

  const sendReply = (): Promise<void> =>
    run(
      () => addWistleBlowingComments({ comment: reply.trim() }, id),
      'Reply sent.',
      () => {
        setReply('')
        comments.refetch()
      }
    )

  const rows: [string, string | undefined][] = [
    ['Report No', record?.reportNo],
    ['Subject', record?.subject],
    ['Description', record?.description],
    ['Report Type', record?.reportType],
    ['Status', record?.status],
    ['Date Reported', record && formatDate(record.createdAt)],
  ]

  return (
    <div className="flex flex-col gap-6 w-full">
      <p
        onClick={onBack}
        className="cursor-pointer w-fit text-sm font-semibold text-text3"
      >
        &larr; All reports
      </p>

      {report.isLoading && (
        <div className="flex justify-center items-center">
          <Loading size="large" />
        </div>
      )}

      {record && (
        <>
          <div className="overflow-hidden rounded-[16px] w-full">
            <table className="rounded-[16px] overflow-hidden border w-full border-white">
              <tbody>
                {rows.map(([label, value]) => (
                  <tr key={label} className="bg-gray1">
                    <td className="bg-gray2 px-4 text-text1 font-semibold text-[14px] w-[30%] h-[48px] py-2 border border-white">
                      {label}
                    </td>
                    <td className="px-4 text-text1 text-[14px] h-[48px] py-2 border border-white whitespace-pre-wrap">
                      {value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {canManage && (
            <div className="w-[200px]">
              <UIButton
                label={isClosed ? 'Reopen report' : 'Close report'}
                onClick={changeStatus}
                loading={isSaving}
                disabled={isSaving}
              />
            </div>
          )}

          <div className="flex flex-col gap-3">
            <p className="text-text2 text-[14px] font-semibold">Comments</p>
            {!comments.data?.length && (
              <p className="text-sm text-text3">No comments yet.</p>
            )}
            {comments.data?.map((item) => (
              <div key={item.id} className="rounded-xl p-3 bg-gray1">
                <p className="text-sm text-text1 whitespace-pre-wrap">
                  {item.comment}
                </p>
                <p className="text-xs text-text3 mt-1">
                  {formatDate(item.createdAt)}
                </p>
              </div>
            ))}
            {canManage && (
              <>
                <UITextArea
                  label="Reply"
                  placeholder="Write a reply"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                />
                <p className="text-xs text-text3">
                  Replies are visible to the person who made the report.
                </p>
                <div className="w-[200px]">
                  <UIButton
                    label="Send reply"
                    onClick={sendReply}
                    loading={isSaving}
                    disabled={isSaving || !reply.trim()}
                  />
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default WhistleblowingCaseView
